import { Box, Button, CardMedia, Checkbox, Dialog, FormControlLabel, Grid, IconButton, LinearProgress, TextField, Theme, Typography, styled, useTheme } from "@mui/material";
import { CommonLayout } from "../../commonComponents";

import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
// NOTE: settlement details still reuses the payment details endpoint (getPaymentDetails).
// Swap it for the dedicated settlement details endpoint once available.
import { getCheckerDetails, getOfflinePaymentApproval, getPaymentDetails, getSettlementApproval } from "../../../redux-store/actions/orderApproval";
import { CheckerOrderApprovalRes, CheckerOrderDetailsReq, CheckerOrderDetailsRes, OfflinePaymentApprovalRes } from "../../../redux-store/types/orderApproval";
import { useHistory } from "react-router";
import dayjs from "../../../utils/dayjs";
import { LoadingButton } from "@mui/lab";
import { DialogCloseIcon, DownloadIcon } from "../../../assets/SvgIcons";
import { Formik, FormikValues } from "../../../lib/formik/Formik";
import { MFTextField } from "../../../lib/formik";
import * as Yup from 'yup';
import { showError } from "../../../redux-store/actions/auth";
import { getBankBalance } from "../../../redux-store/actions/orderManagement";
import { GetBankBalanceResponseBody } from "../../../redux-store/types/orderManagement";
import { RootStateType } from "../../../redux-store/reducers";
import { formatFileSize, isModuleAllowed, toINR } from "../../../utils/utilityFunctions";
import { BANK_SUB_ACCOUNT_TYPES } from "../../../utils/constant";




const Item = styled(Typography)(({ theme }: { theme: Theme }) => ({
    fontSize: '16px',
    lineHeight: '20px',
    letterSpacing: '0.02px',
    color: theme.palette.text.secondary,
    paddingBottom: '7px',
    fontWeight: 500,
    fontFamily: `${theme.typography.fontFamily}`
}));

const ItemDetails = styled(Typography)(({ theme }: { theme: Theme }) => ({
    color: theme.palette.text.primary,
    textTransform: 'capitalize',
    fontWeight: 500,
    fontFamily: `${theme.typography.fontFamily}`
}));
export default function SettlementDetails(): JSX.Element {
    const dispatch = useDispatch();
    const history = useHistory();
    const [settlementDetailsLoading, setSettlementDetailsLoading] = useState<boolean>(false);
    const { productOrderId, product, id, cartId, paymentStatus } = history.location.state as CheckerOrderDetailsReq;
    const [clientDetails, setClientDetails] = useState<{ title: string, description: string }[]>([]);
    const [orderDetails, setOrderDetails] = useState<any>([]); // BP-08: typed as any intentionally — response shape can be either array or object depending on the API; optional chaining (orderDetails?.orderList) handles both safely.
    const [bankDetails, setBankDetails] = useState<{ title: string, description: string }[]>([]);
    const [settlementDetails, setSettlementDetails] = useState<{ title: string, description: string }[]>([]);
    const [documents, setDocuments] = useState<any[]>([]);
    const [continueLoading, setContinueLoading] = useState<{ approve: boolean, reject: boolean, offlinePaymentApprove: boolean }>({} as { approve: boolean, reject: boolean, offlinePaymentApprove: boolean });
    const [openRejectDialog, setOpenRejectDialog] = useState<boolean>(false);
    const [openApproveDialog, setOpenApproveDialog] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [paymentApprovalDialog, setPaymentApprovalDialog] = useState<boolean>(false);
    const [paymentsMessage, setPaymentsMessage] = useState<string>('');
    const [approverId, setApproverId] = useState<string>('');
    const [isSuccessCheck, setIsSuccessCheck] = useState<boolean>(false);
    const [offlinePaymentStatus, setOfflinePaymentStatus] = useState<boolean>(false)
    const [referenceNumber, setReferenceNumber] = useState<string>('');
    const theme = useTheme();

    const { locale, currencyCode, currencySymbol } = useSelector((store: RootStateType) => store.currencyInfo);

    // NOTE (CORR-28): modifiedOrderDetails recomputes on every render. This is acceptable given
    // orderDetails updates infrequently (only on fetch). Wrap in useMemo if performance becomes a concern.
    const modifiedOrderDetails = orderDetails?.orderList?.map((order: Record<string, any>) => {
        const txnType = order?.transactionType?.toLowerCase();
        const isPMS = order?.product?.toLowerCase() === 'pms';
        const isUnlisted = order?.product?.toLowerCase() === 'unlisted equity'
        const isAif = order?.product?.toLowerCase() === 'aif'
        const isBonds = order?.product?.toLowerCase() === 'bonds'
        const isMargin = order?.product?.toLowerCase() === 'margin money'
        const isCorpus = order?.product?.toLowerCase() === 'corpus in/out'
        const isFeePayment = order?.product?.toLowerCase() === 'fee payment'
        const isProductMf = (order?.product ?? "").toLowerCase() === 'mf'

        const showQuantity = !((isPMS && (txnType === 'purchase' || txnType === 'stp purchase')) || (isPMS && txnType === 'withdrawal') || isMargin || isCorpus || isFeePayment)

        const showRate = !(isProductMf || isAif || (isPMS && (txnType === 'purchase' || txnType === 'stp purchase')) || (isPMS && txnType === 'withdrawal') || isBonds || isMargin || isCorpus || isFeePayment)

        const showCharges = !(isProductMf || isAif || (isPMS && (txnType === 'purchase' || txnType === 'stp purchase')) || (isPMS && txnType === 'withdrawal') || isBonds || isMargin || isCorpus || isFeePayment)

        const showCommitmentAmt = !(isProductMf || isUnlisted || (isPMS && txnType === 'withdrawal') || isBonds || isMargin || isCorpus || isFeePayment)

        const showSecurity = !(isMargin || isCorpus || isFeePayment)

        const showIssuer = !(isMargin || isCorpus || isFeePayment)

        const showFolioNumber = (isAif || isPMS)

        return (
            [
                { title: "Order Number", description: order?.orderNumber ?? "-" },
                { title: "Order Date", description: order?.orderDate ? dayjs(order?.orderDate).format("DD/MM/YYYY") : "-" },
                ...(showSecurity
                    ?
                    product === 'MF' && ['switch', 'stp'].includes(txnType)
                        ?
                        [
                            {
                                title: "From Scheme Name",
                                description: order?.securityName ?? "-"
                            },
                            {
                                title: "To Scheme Name",
                                description: order?.toSchemeName ?? "-"
                            }
                        ]
                        :
                        [{
                            title: "Security",
                            description: order?.securityName ?? "-"
                        }]
                    :
                    []
                ),
                ...(showFolioNumber
                    ?
                    [{
                        title: "Folio Number",
                        description: order?.folioNumber ?? "-"
                    }]
                    :
                    []
                ),
                ...(showIssuer
                    ?
                    [{
                        title: "Issuer",
                        description: order?.issuer ?? "-"
                    }]
                    :
                    []
                ),
                { title: "Transaction Type", description: order?.transactionType },
                ...(txnType === "xsip" ? [
                    {
                        title: "Frequency",
                        description: order?.frequency ?? "-"
                    },
                    {
                        title: 'No. of Installments',
                        description: order?.noOfInstallments ?? "-"
                    },
                    {
                        title: 'SIP Date',
                        description: order?.startDate ?? "-"
                    },
                    {
                        title: 'Mandate Bank Name',
                        description: order?.bankName ?? "-"
                    },
                    {
                        title: 'Mandate End Date',
                        description: order?.mandateEndDate ? dayjs(order.mandateEndDate).format("DD/MM/YYYY") : "-"
                    },
                    {
                        title: 'Mandate Account Number',
                        description: order?.accountNo ?? "-"
                    },
                    {
                        title: 'Mandate ID',
                        description: order?.mandateId ?? "-"
                    }
                ] : []),
                ...(txnType === "swp" ? [
                    {
                        title: "Frequency",
                        description: order?.frequency ?? "-"
                    },
                    {
                        title: 'No. of Installments',
                        description: order?.noOfInstallments ?? "-"
                    },
                    {
                        title: 'SWP Date',
                        description: order?.startDate ?? "-"
                    }
                ] : []),
                ...(txnType === "stp" ? [
                    {
                        title: "Frequency",
                        description: order?.frequency ?? "-"
                    },
                    ...((order?.frequency ?? "").toLowerCase() === "daily"
                        ? [
                            {
                                title: 'STP Start Date',
                                description: order?.startDate ?? "-"
                            },
                            {
                                title: 'STP End Date',
                                description: order?.endDate ?? "-"
                            }
                        ]
                        : [
                            {
                                title: 'STP Date',
                                description: order?.startDate ?? "-"
                            },
                            {
                                title: 'No. of Installments',
                                description: order?.noOfInstallments
                            }
                        ]
                    )
                ] : []),
                {
                    title: "Amount",
                    description: orderDetails?.orderList?.[0]?.product?.toLowerCase() !== 'mf'
                        ? toINR({ value: order?.investmentAmount ?? 0, locale, currencyCode })
                        : toINR({ value: order?.amount ?? 0, locale, currencyCode })
                },
                ...(showQuantity
                    ?
                    [
                        { title: "Quantity", description: order?.quantity }
                    ]
                    :
                    []
                ),
                ...(txnType === "redemption" ? [{
                    title: "Redeem All",
                    description: (`${order?.allRedeemFlag ?? ""}`).toLowerCase() === "y" ? "Yes" : "No"
                }] : []),
                ...(showRate
                    ?
                    [{
                        title: "Rate",
                        description: toINR({ value: order?.rate ?? 0, locale, currencyCode })
                    }]
                    :
                    []
                ),
                ...(showCharges
                    ?
                    [{
                        title: "Charges",
                        description: toINR({ value: order?.charges ?? 0, locale, currencyCode })
                    }]
                    :
                    []
                ),
                ...(isMargin
                    ?
                    [{ title: "Broker Name", description: order?.brokerName ?? "-" }]
                    :
                    []
                ),
                ...(['bonds', 'unlisted equity'].includes(order?.product?.toLowerCase())
                    ?
                    [
                        {
                            title: "Counter Party Name",
                            description: order?.counterPartyName ?? "-"
                        },
                        {
                            title: "Counter Party",
                            description: order?.counterParty ?? "-"
                        }
                    ]
                    :
                    []),
                ...(isBonds
                    ?
                    [
                        {
                            title: "Stamp Duty",
                            description: order?.stampDuty ?? "-"
                        },
                        {
                            title: "Asset Class",
                            description: order?.assetClass ?? "-"
                        },
                        {
                            title: "Price Type",
                            description: order?.priceType ?? "-"
                        },
                        {
                            title: `${order?.priceType ?? "Clean Price"}`,
                            description: toINR({ value: order?.cleanPrice ?? 0, locale, currencyCode })
                        },
                        {
                            title: "Acquistion Cost",
                            description: toINR({ value: order?.acquisitionCost ?? 0, locale, currencyCode })
                        },
                        {
                            title: "Accured Interest",
                            description: toINR({ value: order?.accuredInterest ?? 0, locale, currencyCode })
                        },
                        {
                            title: "SettlementAmount",
                            description: toINR({ value: order?.settlementAmount ?? 0, locale, currencyCode })
                        },
                    ]
                    :
                    []
                ),
                ...(isUnlisted
                    ?
                    [{
                        title: "Stamp Duty",
                        description: "",
                        type: "checkbox",
                        checked: `${order?.stampDuty ?? ""}` === "1"
                    }]
                    :
                    []
                ),
                ...(showCommitmentAmt
                    ?
                    [{
                        title: "Commitment Amount",
                        description: toINR({ value: order?.commitmentAmount ?? 0, locale, currencyCode })
                    }]
                    :
                    []
                ),
                ...(isAif
                    ?
                    [{
                        title: "Last Available Nav",
                        description: toINR({ value: order?.lastAvailableNav ?? 0, locale, currencyCode })
                    },
                    {
                        title: "Current Market Value",
                        description: toINR({ value: order?.marketValue ?? 0, locale, currencyCode })
                    }]
                    :
                    []
                ),
                ...(isPMS && txnType === 'withdrawal' ?
                    [{
                        title: 'Current Portfolio Value',
                        description: toINR({ value: order?.currentPortfolioValue ?? 0, locale, currencyCode })
                    },
                    {
                        title: 'Last Valuation Date',
                        description: order?.lastValuationDate ? dayjs(order?.lastValuationDate).format("DD/MM/YYYY") : "-"
                    }]
                    :
                    []
                ),
                ...(!isProductMf ? [
                    { title: "Remarks", description: order?.rmRemarks ?? "-" }
                ] : []),
            ]
        )
    }
    )

    const fetchSettlementDetails = async () => {
        // NOTE (CORR-29): setSettlementDetailsLoading(false) is called in the finally block below — confirmed.
        try {
            setSettlementDetailsLoading(true);
            const response = await (dispatch(getPaymentDetails({ productOrderId, product, orderId: id })) as unknown as Promise<CheckerOrderDetailsRes>);

            if (!response?.isSuccess) {
                dispatch(showError(response?.message));
                setIsError(true);
                return; // NOTE (CORR-36): return early — no point calling getBankBalance or mapping details on a failed response
            }

            setOrderDetails(response);

            const iciciRes = await (dispatch(getBankBalance({
                investorBankAccount: response?.bankDetails?.bankAccNo ?? "",
                clientCode: `${response?.orderList?.[0]?.clientCode ?? ""}`,
                investorIfscCode: `${response?.orderList?.[0]?.investorBankIfscCode ?? ""}`
            })) as unknown as Promise<GetBankBalanceResponseBody>);

            setClientDetails([
                { title: "Product", description: response?.orderList?.[0]?.product ?? "-" },
                { title: "Client Name", description: response?.orderList?.[0]?.clientName ?? "-" },
                { title: "Client Code", description: response?.orderList?.[0]?.clientCode ?? "-" },
                { title: "Head Of Family", description: `${response?.isFamilyHead ?? ""}`.toLowerCase() === "y" ? "Yes" : "No" },
                { title: "Account", description: response?.orderList?.[0]?.portfolioName ?? "-" },
                { title: "Account Type", description: response?.orderList?.[0]?.accountType ?? "-" },
                { title: "Investor Type", description: response?.investorType ?? "-" },
                { title: "Mode Of Holding", description: response?.modeOfHolding ?? "-" },
                { title: "Email ID", description: response?.orderList?.[0]?.emailId ?? "-" },
                {
                    title: "Order Placed By",
                    description: response?.orderPlacedByUser?.name ?? "-"
                },
            ])

            setBankDetails([
                { title: "Account Holder", description: response?.bankDetails?.bankAccHolder ?? "-" },
                { title: "Account No.", description: response?.bankDetails?.bankAccNo ?? "-" },
                { title: "Account Type", description: response?.bankDetails?.bankAccType ?? "-" },
                { title: "Sub Account Type", description: `${BANK_SUB_ACCOUNT_TYPES[`${response?.bankDetails?.subAccountType ?? ""}`.toUpperCase()] ?? "-"}` },
                { title: "Bank Name", description: response?.bankDetails?.bankName ?? "-" },
                { title: "IFSC Code", description: response?.bankDetails?.ifscCode ?? "-" },
                {
                    title: "Available Bank Balance",
                    // NOTE (CORR-21): parseInt(currencySymbol, 16) is the established project-wide pattern for rendering the currency symbol.
                    // currencySymbol in Redux store is always a hex code point string (e.g. "20B9" for ₹) — safe to use parseInt(..., 16) here.
                    description: `${String.fromCharCode(parseInt(currencySymbol, 16))} ${(!Number.isNaN(Number(iciciRes?.bankBalance ?? 0)) ? Number(iciciRes?.bankBalance ?? 0) : 0)?.toLocaleString(locale)}`
                },
                {
                    title: "Utilized Balance",
                    description: `${String.fromCharCode(parseInt(currencySymbol, 16))} ${(!Number.isNaN(Number(iciciRes?.utilizedAmount ?? 0)) ? Number(iciciRes?.utilizedAmount ?? 0) : 0)?.toLocaleString(locale)}`
                },
                {
                    title: "Net Available Balance",
                    description: `${String.fromCharCode(parseInt(currencySymbol, 16))} ${(!Number.isNaN(Number(iciciRes?.netAmount ?? 0)) ? Number(iciciRes?.netAmount ?? 0) : 0)?.toLocaleString(locale)}`
                },
            ])


            setSettlementDetails([
                { title: "Settlement Method", description: response?.orderList?.[0]?.settlementMethod ?? "-" },
                { title: "Settlement Date", description: response?.orderList?.[0]?.settlementDate ? dayjs(response?.orderList?.[0]?.settlementDate).format("DD/MM/YYYY") : '-' },
                { title: "Cash Settlement Reference Number", description: response?.orderList?.[0]?.referenceNumber ?? "-" },
                // Stock Settlement Reference Number is rendered as an editable field below the mapped values
                ...(['unlisted equity', 'bonds'].includes(response?.orderList?.[0]?.product?.toLowerCase())
                    ? [{ title: "Settlement Bank Source", description: response?.orderList?.[0]?.bankSource ?? "-" }]
                    : []
                ),
                { title: "Settlement Bank", description: response?.orderList?.[0]?.settlementBank ?? "-" },
                { title: "Settlement Bank Number", description: response?.orderList?.[0]?.settlementBankNumber ?? "-" },
                { title: "Settlement Bank Sub Account Type", description: response?.orderList?.[0]?.settlementBankSubAccountType ?? "-" },
                { title: "Settlement Bank IFSC Code", description: response?.orderList?.[0]?.settleBankIfscCode ?? "-" },
                ...(['aif', 'unlisted equity', 'bonds'].includes(response?.orderList?.[0]?.product?.toLowerCase())
                    ? [
                        { title: "Depository Name", description: response?.orderList?.[0]?.depositoryName ?? "-" },
                        { title: "Dp Name", description: response?.orderList?.[0]?.dpName ?? "-" },
                        { title: "Dp Id", description: response?.orderList?.[0]?.settlementDpId ?? "-" },
                        { title: "Client Id", description: response?.orderList?.[0]?.clientId ?? "-" },
                    ]
                    :
                    []
                ),
            ])

            setDocuments(response?.orderDocumentIds ?? []);
            // Pre-fill stock settlement reference number from backend if already set.
            // NOTE (SAN-02): stockSettlementReferenceNo is a new backend field distinct from referenceNumber (cash settlement).
            // Backend sends both fields independently; referenceNumber continues to hold the cash settlement value.
            if (response?.orderList?.[0]?.stockSettlementReferenceNo) {
                setReferenceNumber(response.orderList[0].stockSettlementReferenceNo)
            }

        } catch (error) {
            console.error('Error fetching settlement details:', error);
        } finally {
            setSettlementDetailsLoading(false);
        }
    };

    const fetchCheckerDetails = async () => {
        const checkerDetailsRes = await (dispatch(getCheckerDetails()) as unknown as Promise<any>);
        setApproverId(checkerDetailsRes?.approverId ?? "")
    }

    const handleSettlementDetailsContinue = async (text: string, remarks: string) => {
        try {
            setContinueLoading({ approve: text === 'approve', reject: text === 'reject', offlinePaymentApprove: false });

            const payload = {
                productOrderId: productOrderId,
                product: product,
                orderId: id ?? '',
                isApproved: text === 'approve' ? true : false,
                settlementCheckerId: approverId,
                // NOTE (SAN-03): stockSettlementReferenceNo is the new backend contract key for stock settlement reference.
                // The settlement-approval API accepts this field alongside the existing referenceNumber (cash settlement).
                stockSettlementReferenceNo: referenceNumber?.trim(),
                remarks,
                cartId
            }

            const response = await (dispatch(getSettlementApproval(payload)) as unknown as Promise<CheckerOrderApprovalRes>);

            if (response?.isSuccess) {
                setIsSuccessCheck(true);
                setPaymentApprovalDialog(true);
                setPaymentsMessage(response?.message);
            }
            else {
                dispatch(showError(response?.message))
            }
        } catch (error) {
            console.log('error', error);
        }
        finally {
            setContinueLoading({ approve: false, reject: false, offlinePaymentApprove: false });
        }
    }

    const rejectSettlement = async (values: FormikValues) => {
        const { rejectedReason } = values;
        try {
            setOpenRejectDialog(false);
            await handleSettlementDetailsContinue('reject', rejectedReason);
        } catch (error) {
            console.log('error', error);
        }
    }

    const approveSettlement = async () => {
        try {
            setOpenApproveDialog(false);
            await handleSettlementDetailsContinue('approve', '');
        } catch (error) {
            console.log('error', error);
        }
    }

    const handleOfflinePaymentStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOfflinePaymentStatus(event.target.checked);
    };

    const handleOfflinePaymentStatusSubmit = async () => {
        try {
            setContinueLoading({ approve: false, reject: false, offlinePaymentApprove: true });

            const payload = {
                orderId: id ?? '',
                productOrderId: productOrderId,
                paymentCheckerId: approverId,
                product: product,
                isApproved: offlinePaymentStatus,
                remarks: "",
                cartId
            }

            const response = await (dispatch(getOfflinePaymentApproval(payload)) as unknown as Promise<OfflinePaymentApprovalRes>);

            if (response?.isSuccess) {
                setIsSuccessCheck(true);
                setPaymentApprovalDialog(true);
                setPaymentsMessage(response?.message);
            }
            else {
                dispatch(showError(response?.message))
            }
        } catch (error) {
            console.log('error', error);
        }
        finally {
            setContinueLoading({ approve: false, reject: false, offlinePaymentApprove: false });
        }
    }

    useEffect(() => {
        fetchSettlementDetails();
        fetchCheckerDetails();
    }, [])

    // CORR-02 (intended business/backend behavior): the stock settlement reference is a hard
    // requirement to approve ANY settlement that reaches this screen. Per backend contract, every
    // settlement routed here is a stock settlement and must carry a stock settlement reference
    // before it can be approved — there is deliberately no cash-only approval path from this screen.
    // Cash settlement is handled on a separate flow; the cash reference above is read-only context only.
    // Hence disableApproveButton gating on !isStockSettlementRefMissing is correct and intentional.
    const isStockSettlementRefMissing = !referenceNumber?.trim()
    const isSettlementPending = ['pending', 'settlement pending'].includes(paymentStatus?.toLowerCase() ?? '')
    const showStockSettlementRefEditable = ['pending', 'settlement pending', 'payment pending'].includes(paymentStatus?.toLowerCase() ?? '')
    const disableRejectButton = continueLoading?.reject || !isSettlementPending || isError || isSuccessCheck || continueLoading?.approve
    const disableApproveButton = continueLoading?.approve || !isSettlementPending || isError || isSuccessCheck || continueLoading?.reject || isStockSettlementRefMissing


    return (
        <CommonLayout>
            <>
                <Box>
                    <Typography sx={{ color: 'text.primary', mb: 2.5 }} variant='h1'>
                        Settlement Details
                    </Typography>
                    {settlementDetailsLoading ? <LinearProgress /> : (
                        <Box sx={(theme: Theme) => ({ padding: '35px 25px', borderRadius: '25px', border: `1px solid ${theme.palette.text.primary}` })}>

                            {/* Offline Payment Status */}
                            {isModuleAllowed({ value: paymentStatus ?? "", compareArr: ['PAYMENT FAILED'] }) && (
                                <Box
                                    sx={(theme: Theme) => ({
                                        padding: '25px',
                                        borderRadius: '10px',
                                        backgroundColor: theme.palette.background.paper,
                                        mb: '40px',
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        rowGap: "10px"
                                    })}
                                >
                                    <Typography
                                        sx={(theme: Theme) => ({
                                            fontSize: 18,
                                            fontWeight: 700,
                                            color: theme.palette.primary[900],
                                            mb: "8px"
                                        })}
                                    >
                                        Offline Payment Status
                                    </Typography>

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={offlinePaymentStatus}
                                                onChange={handleOfflinePaymentStatusChange}
                                                sx={(theme: Theme) => ({
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 500,
                                                })}
                                                disabled={continueLoading.offlinePaymentApprove}
                                            />
                                        }
                                        label="Is Payment Successful?"
                                    />

                                    <LoadingButton
                                        loading={continueLoading.offlinePaymentApprove}
                                        disabled={!offlinePaymentStatus}
                                        loadingPosition="center"
                                        variant="outlined"
                                        onClick={handleOfflinePaymentStatusSubmit}
                                        sx={(theme: Theme) => ({
                                            borderRadius: '10px',
                                            padding: '10px 12px',
                                            color: theme.palette.background.paper,
                                            background: theme.palette.primary[500],
                                            border: `0.5px solid  ${theme.palette.primary[500]}`,
                                            fontSize: 16,
                                            '&:hover': {
                                                background: theme.palette.primary[500],
                                                border: `0.5px solid  ${theme.palette.primary[500]}`,
                                            },
                                            '&:disabled': {
                                                opacity: 0.5
                                            }
                                        })}
                                    >
                                        Submit
                                    </LoadingButton>
                                </Box>
                            )}

                            {/* Client Bank Details */}

                            <Box sx={(theme: Theme) => ({ padding: '25px', borderRadius: '10px', backgroundColor: theme.palette.background.paper, mb: '40px' })}>
                                <Grid container >
                                    <Grid item xs={12} >
                                        <Typography sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '25px' })}>
                                            Client Bank Details
                                        </Typography>

                                    </Grid>
                                    <Grid container spacing={4}>
                                        {bankDetails.map((item, index) => (
                                            <Grid item xs={12} sm={6} lg={4} key={index}>
                                                <Item>{item.title}</Item>
                                                <ItemDetails>{item.description || '-'}</ItemDetails>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Client Details */}

                            <Box sx={(theme: Theme) => ({ padding: '25px', borderRadius: '10px', backgroundColor: theme.palette.background.paper, mb: '40px' })}>
                                <Grid container >
                                    <Grid item xs={12} >
                                        <Typography sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '25px' })}>
                                            Client  Details
                                        </Typography>

                                    </Grid>
                                    <Grid container spacing={4}>
                                        {clientDetails.map((item, index) => (
                                            <Grid item xs={12} sm={6} lg={4} key={index}>
                                                <Item>{item.title}</Item>
                                                <ItemDetails
                                                    sx={{
                                                        ...(item.title === "Email ID" ? {
                                                            textTransform: "none"
                                                        } : {})
                                                    }}
                                                >
                                                    {item.description || '-'}
                                                </ItemDetails>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            </Box>


                            {/* Order Details */}

                            <Box sx={(theme: Theme) => ({ padding: '25px', borderRadius: '10px', backgroundColor: theme.palette.background.paper, mb: '40px' })}>
                                <Grid container >
                                    <Grid item xs={12} >
                                        <Typography sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '25px' })}>
                                            Order  Details
                                        </Typography>

                                    </Grid>
                                    {modifiedOrderDetails?.map((order: any, orderIndex: number) => (
                                        <Box
                                            key={orderIndex}
                                            sx={(theme: Theme) => ({
                                                border: `1px solid ${theme.palette.grey[200]}`,
                                                borderRadius: '10px',
                                                padding: '25px',
                                                mb: orderIndex === modifiedOrderDetails.length - 1 ? 0 : 2,
                                                background: theme.palette.common.white,
                                                width: '100%'
                                            })}
                                        >
                                            <Grid container spacing={3}>
                                                {order?.map((subOrder: any, subOrderIndex: number) => (
                                                    <Grid
                                                        key={`${orderIndex}-${subOrderIndex}`}
                                                        item
                                                        xs={12}
                                                        sm={6}
                                                        lg={4}
                                                    >
                                                        <Item>{subOrder?.title}</Item>
                                                        {subOrder?.type === "checkbox" ? (
                                                            <Checkbox
                                                                checked={!!subOrder?.checked}
                                                                disabled
                                                                sx={{ p: 0 }}
                                                            />
                                                        ) : (
                                                            <ItemDetails
                                                                sx={{ wordBreak: 'break-word' }}
                                                            >
                                                                {subOrder?.description || '-'}
                                                            </ItemDetails>
                                                        )}
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </Box>
                                    ))}
                                </Grid>
                            </Box>

                            {/* Settlement Details */}

                            <Box sx={(theme: Theme) => ({ padding: '25px', borderRadius: '10px', backgroundColor: theme.palette.background.paper, mb: '40px' })}>
                                <Grid container >
                                    <Grid item xs={12} >
                                        <Typography sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '25px' })}>
                                            Settlement  Details
                                        </Typography>

                                    </Grid>
                                    <Grid container spacing={4}>
                                        {settlementDetails.map((item, index) => (
                                            <Grid item xs={12} sm={6} lg={4} key={index}>
                                                <Item>{item.title}</Item>
                                                <ItemDetails>{item.description || '-'}</ItemDetails>
                                            </Grid>
                                        ))}
                                        <Grid item xs={12} sm={6} lg={4}>
                                            {showStockSettlementRefEditable ? (
                                                // Editable TextField — user can still approve/reject
                                                <>
                                                    <Item>Stock Settlement Reference Number</Item>
                                                    <TextField
                                                        fullWidth
                                                        value={referenceNumber}
                                                        placeholder="Enter Stock Settlement Reference Number"
                                                        onChange={(e) => setReferenceNumber(e.target.value)}
                                                        disabled={isSuccessCheck || isError || !isSettlementPending}
                                                        error={isStockSettlementRefMissing}
                                                        helperText="Stock Settlement Reference Number is required to approve"
                                                        inputProps={{ maxLength: 50 }}
                                                        InputProps={{ style: { borderRadius: '5px', height: '40px', fontSize: '16px' } }}
                                                    />
                                                </>
                                            ) : (
                                                // Read-only label/value — order already processed
                                                <>
                                                    <Item>Stock Settlement Reference Number</Item>
                                                    <ItemDetails>{referenceNumber || '-'}</ItemDetails>
                                                </>
                                            )
                                            }
                                        </Grid >
                                    </Grid >
                                </Grid >
                            </Box >

                            {/* Order Supporting Document */}

                            < Box sx={(theme: Theme) => ({ padding: '25px', borderRadius: '10px', backgroundColor: theme.palette.background.paper, mb: '40px' })
                            }>
                                <Grid container >
                                    <Grid item xs={12} >
                                        <Typography sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '25px' })}>
                                            Order Supporting Document
                                        </Typography>

                                        {documents?.map((item, index) => (
                                            <Grid
                                                key={index}
                                                container
                                                xs={6}
                                                md={4}
                                                sx={(theme: Theme) => ({
                                                    border: `0.5px solid ${theme.palette.text.secondary}80`,
                                                    borderRadius: "10px",
                                                    padding: "15px 15px 15px 5px",
                                                    background: `${theme.palette.common.white}0D`,
                                                    mt: 3,
                                                    ml: 0.5
                                                })}
                                                columnSpacing="10px"
                                            >
                                                <Grid item container justifyContent="center" alignItems="center" xs={2}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        src={item?.thumbnailUrl ?? '/images/photo-placeholder.jpg'}
                                                        alt="Thumbnail"
                                                        width="60"
                                                        height="60"
                                                        sx={{
                                                            objectFit: "contain"
                                                        }}
                                                    />
                                                </Grid>
                                                <Grid item container xs={6}>
                                                    <Grid item xs={12}>
                                                        <Typography
                                                            variant='subtitle_500'
                                                            color="text.primary"
                                                        >
                                                            {item?.documentType ?? "-"}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Typography
                                                            variant='overline_400'
                                                            color="text.secondary"
                                                        >
                                                            {formatFileSize(item?.size ?? 0)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Button
                                                            component="span"
                                                            onClick={() => window.open(item?.viewUrl ?? '', '_blank')}
                                                            sx={{ cursor: "pointer", color: theme.palette.primary[500], ...theme.typography.overline_600 }}
                                                            aria-label='Click to view Document'
                                                            role='link'
                                                        >
                                                            Click to view
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                                <Grid
                                                    item
                                                    container
                                                    xs={4}
                                                >
                                                    <Grid item container justifyContent="flex-end" alignItems="center" xs={12}>
                                                        <IconButton
                                                            aria-label='Download document'
                                                            onClick={() => window.open(item?.url ?? '', '_blank')}
                                                            sx={{ cursor: "pointer" }}
                                                        >
                                                            <DownloadIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Grid >
                                        ))}
                                    </Grid>
                                </Grid>
                            </Box >

                            {/* Buttons */}

                            < Grid container spacing={3} sx={{ mt: '40px', }}>
                                <Grid item xs={12} sm={6}>
                                    <LoadingButton
                                        fullWidth
                                        loading={continueLoading?.reject}
                                        disabled={disableRejectButton}
                                        loadingPosition="start"
                                        onClick={() => setOpenRejectDialog(true)}
                                        variant="outlined"
                                        sx={(theme: Theme) => ({
                                            borderRadius: '10px',
                                            padding: '15px',
                                            color: theme.palette.text.secondary,
                                            border: `0.5px solid  ${theme.palette.text.secondary}`,
                                            '&:hover': {
                                                background: 'unset',
                                                border: `0.5px solid  ${theme.palette.text.secondary}`,
                                            }
                                        })}>
                                        Reject
                                    </LoadingButton>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <LoadingButton
                                        fullWidth
                                        loading={continueLoading?.approve}
                                        disabled={disableApproveButton}
                                        loadingPosition="start"
                                        onClick={() => setOpenApproveDialog(true)}
                                        variant="outlined"
                                        sx={(theme: Theme) => ({
                                            borderRadius: '10px',
                                            padding: '15px',
                                            color: theme.palette.background.paper,
                                            background: theme.palette.primary[500],
                                            border: `0.5px solid  ${theme.palette.primary[500]}`,
                                            '&:hover': {
                                                background: theme.palette.primary[500],
                                            }
                                        })}>
                                        Approve
                                    </LoadingButton>
                                </Grid>
                            </Grid >
                        </Box >
                    )}
                </Box >
                <Dialog
                    onClose={() => setOpenRejectDialog(false)}
                    open={openRejectDialog}
                    sx={{
                        '.MuiPaper-root ': {
                            p: 4,
                            width: { xs: '70%', sm: '70%' },
                        },
                    }}>
                    <IconButton onClick={() => setOpenRejectDialog(false)} sx={{ alignSelf: 'flex-end' }} aria-label="close-reject-dialog">
                        <DialogCloseIcon />
                    </IconButton>
                    <Box sx={{ mb: 2 }}>
                        <CardMedia
                            component="img"
                            src="/images/warning-icon-red.svg"
                            sx={{ width: 'unset', mx: 'auto' }}
                        />
                    </Box>
                    <Box
                        sx={{
                            width: { xs: '100%', sm: '80%' },
                            mx: 'auto',
                            '.MuiTypography-root': {
                                fontFamily: `${theme.typography.fontFamily}`,
                                fontSize: 18,
                                fontWeight: 500,
                                textAlign: 'center',
                            },
                        }}>
                        <Typography sx={(theme: Theme) => ({ color: theme.palette.primary[500] })}>
                            Are you sure you want to{' '}
                            <Typography component="span" sx={(theme: Theme) => ({ color: theme.palette.error[500] })}>
                                Reject
                            </Typography>{' '}
                            the settlement
                        </Typography>
                        <Typography sx={(theme: Theme) => ({ color: theme.palette.error[500], mt: 5 })}>
                            {' '}
                            Give reason for application rejection *
                        </Typography>
                        <Formik
                            initialValues={{ rejectedReason: '' }}
                            onSubmit={rejectSettlement}
                            validationSchema={
                                Yup.object().shape({
                                    rejectedReason: Yup.string().required('Rejected reason is required'),
                                })
                            }>
                            {({ handleSubmit }) => (
                                <Box component="form" noValidate onSubmit={handleSubmit}>
                                    <MFTextField
                                        name="rejectedReason"
                                        multiline
                                        rows={4}
                                        placeholder="Type your answer here..."
                                    />

                                    <Grid container sx={{ my: 3, justifyContent: 'center', gap: 1.5 }}>
                                        <Button
                                            onClick={() => {
                                                setOpenRejectDialog(false);
                                            }}
                                            variant="outlined"
                                            sx={(theme: Theme) => ({
                                                fontSize: 15,
                                                color: theme.palette.text.secondary,
                                                minWidth: 180,
                                                letterSpacing: 2,
                                                fontWeight: 500,
                                            })}>
                                            <Typography variant="body_500">Cancel</Typography>
                                        </Button>
                                        <Button
                                            variant="contained"
                                            sx={{
                                                fontSize: 15,
                                                minWidth: 180,
                                                letterSpacing: 2,
                                                fontWeight: 400,
                                            }}
                                            type="submit">
                                            <Typography variant="body_500">Continue</Typography>
                                        </Button>
                                    </Grid>
                                </Box>
                            )}
                        </Formik>
                    </Box>
                </Dialog>
                <Dialog
                    onClose={() => setOpenApproveDialog(false)}
                    open={openApproveDialog}
                    sx={{
                        '.MuiPaper-root ': {
                            p: 4,
                            width: { xs: '70%', sm: '70%' },
                        },
                    }}>
                    <IconButton onClick={() => setOpenApproveDialog(false)} sx={{ alignSelf: 'flex-end' }} aria-label="close-approve-dialog">
                        <DialogCloseIcon />
                    </IconButton>
                    <Box
                        sx={{
                            width: { xs: '100%', sm: '80%' },
                            mx: 'auto',
                            '.MuiTypography-root': {
                                fontSize: 20,
                                fontWeight: 500,
                                textAlign: 'center',
                            },
                        }}>
                        <Typography sx={{ color: 'primary.main' }}>
                            Are you sure you want to{' '}
                            <Typography component="span" sx={{ color: 'green' }}>
                                Approve
                            </Typography>{' '}
                            the settlement
                        </Typography>
                    </Box>
                    <Grid container sx={{ my: 3, justifyContent: 'center', gap: 1.5 }}>
                        <Button
                            onClick={() => {
                                setOpenApproveDialog(false);
                            }}
                            variant="outlined"
                            sx={{
                                fontSize: 15,
                                color: 'primary.main',
                                minWidth: 180,
                                letterSpacing: 2,
                                fontWeight: 500,
                            }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                fontSize: 15,
                                minWidth: 180,
                                letterSpacing: 2,
                                fontWeight: 400,
                            }}
                            onClick={approveSettlement}>
                            Continue
                        </Button>
                    </Grid>
                </Dialog>
                <Dialog
                    onClose={() => setPaymentApprovalDialog(false)}
                    open={paymentApprovalDialog}
                    sx={{
                        '.MuiPaper-root ': {
                            p: 4,
                            width: { xs: '70%', sm: '70%' },
                        },
                    }}>
                    <IconButton onClick={() => setPaymentApprovalDialog(false)} sx={{ alignSelf: 'flex-end' }} aria-label="close-settlement-approval-dialog">
                        <DialogCloseIcon />
                    </IconButton>
                    <Box
                        sx={{
                            width: { xs: '100%', sm: '80%' },
                            mx: 'auto',
                            '.MuiTypography-root': {
                                fontSize: 20,
                                fontWeight: 500,
                                textAlign: 'center',
                            },
                        }}>
                        <Typography variant="body_500" sx={(theme: Theme) => ({ color: theme.palette.text.primary })}>
                            {paymentsMessage ?? ''}
                        </Typography>
                    </Box>
                    <Grid container sx={{ my: 3, justifyContent: 'center', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            sx={{
                                fontSize: 15,
                                minWidth: 180,
                                letterSpacing: 2,
                                fontWeight: 400,
                            }}
                            onClick={() => history.push('/settlements-approval')}>
                            Go To Settlement Dashboard
                        </Button>
                    </Grid>
                </Dialog>
            </>
        </CommonLayout >
    )
}
